/**
 * EmailPreferences Component
 *
 * Toggle switches for managing email notification preferences.
 * Allows users to control which types of emails they receive.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-151 - Shared Account Settings
 *
 * Props:
 * - emailPreferences: object - Current email preferences
 * - onUpdate: function - Callback when preferences are updated successfully
 */

import React, { useState } from 'react';
import apiRequest from '../../services/api';
import { Mail, CheckCircle, AlertCircle, Save } from 'lucide-react';

/**
 * Toggle Switch Component
 */
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2
        ${checked ? 'bg-brand-primary' : 'bg-gray-300'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      aria-label="Toggle notification"
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
          ${checked ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
    </button>
  );
}

/**
 * Email Preferences Component
 */
export default function EmailPreferences({ emailPreferences, onUpdate }) {
  // Form state - initialize with current preferences
  const [preferences, setPreferences] = useState({
    marketing_emails: emailPreferences?.marketing_emails ?? true,
    booking_notifications: emailPreferences?.booking_notifications ?? true,
    review_notifications: emailPreferences?.review_notifications ?? true,
    system_notifications: emailPreferences?.system_notifications ?? true,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  /**
   * Handle toggle change
   */
  const handleToggle = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
    setSuccess(false);
    setError(null);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (  _e) => {
    _e.preventDefault();

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response = await apiRequest('/api/v1/account/email-preferences', {
        method: 'PUT',
        body: JSON.stringify(preferences)
      });

      if (response.success) {
        setSuccess(true);
        setHasChanges(false);
        onUpdate(response.data.data);

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error updating email preferences:', err);
      setError(err.message || 'Failed to update email preferences');
    } finally {
      setLoading(false);
    }
  };

  // Preference configuration
  const preferenceOptions = [
    {
      key: 'marketing_emails',
      title: 'Marketing Emails',
      description: 'Receive updates about new features, promotions, and guides in your area',
    },
    {
      key: 'booking_notifications',
      title: 'Booking Notifications',
      description: 'Get notified about booking confirmations, reminders, and updates',
    },
    {
      key: 'review_notifications',
      title: 'Review Notifications',
      description: 'Receive alerts when you receive new reviews or review-related updates',
    },
    {
      key: 'system_notifications',
      title: 'System Notifications',
      description: 'Important account security and system updates (recommended)',
    },
  ];

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Email Notifications</h3>
      <p className="text-sm text-gray-600 mb-6">
        Control which email notifications you receive. You can unsubscribe at any time.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Preference Toggles */}
        <div className="space-y-4">
          {preferenceOptions.map((option) => (
            <div
              key={option.key}
              className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex-1 mr-4">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 text-gray-600" />
                  <h4 className="text-sm font-semibold text-gray-900">
                    {option.title}
                  </h4>
                </div>
                <p className="text-xs text-gray-600">
                  {option.description}
                </p>
              </div>

              <Toggle
                checked={preferences[option.key]}
                onChange={(value) => handleToggle(option.key, value)}
                disabled={loading}
              />
            </div>
          ))}
        </div>

        {/* Important Notice for System Notifications */}
        {!preferences.system_notifications && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-900 font-medium mb-1">
                System notifications are turned off
              </p>
              <p className="text-xs text-yellow-800">
                You won&apos;t receive important security alerts, password reset emails, or critical account updates.
              </p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800 font-medium">
              Email preferences updated successfully!
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!hasChanges || loading}
            className={`
              flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors
              ${!hasChanges || loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-brand-primary text-white hover:bg-brand-primary-dark'
              }
            `}
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </form>
    </div>
  );
}
